import React from "react";
import {Alert, Box, Typography} from "@mui/material";
import {Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer,
    Tooltip, XAxis, YAxis} from "recharts";
import {finiteNumber, formatMoney} from "./distributor-display";
import ProductPieChart from "./product-pie-chart";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#FF639D", "#C4416C", "#FFA580", "#45A5BD", "#97B650"];
const shortName = name => name?.length > 28 ? `${name.slice(0, 27)}…` : name;
const cells = rows => rows.map((row, index) => <Cell key={`${row.productTenantId || row.name || index}:${row.currency || "unknown"}`} fill={COLORS[index % COLORS.length]}/>);

function SolidBar({x, y, width, height, fill}) {
    if (![x, y, width, height].every(finiteNumber) || width <= 0 || height <= 0) return null;
    // Keep bars as one filled primitive, including hover, without animated paths or inherited SVG styling.
    return <rect x={x} y={y} width={width} height={height} fill={fill} stroke="none" fillOpacity={1}/>;
}

function ProductBars({rows, metric, amount, horizontal, scrollColumns}) {
    const scrollable = horizontal || scrollColumns;
    return <Box sx={{maxHeight: horizontal ? 600 : undefined, overflowY: horizontal ? "auto" : scrollColumns ? "hidden" : undefined,
        overflowX: scrollColumns ? "auto" : undefined, maxWidth: "100%"}}
        tabIndex={scrollable ? 0 : undefined} role={scrollable ? "region" : undefined} aria-label={scrollable ? metric.label : undefined}>
        {/* Keep each column and its label readable; long rankings scroll inside the chart. */}
        <Box sx={{minWidth: scrollColumns ? Math.max(640, rows.length * 90 + 84) : undefined}}>
        <ResponsiveContainer width="100%" height={horizontal ? Math.max(240, rows.length * 56 + 60) : scrollColumns ? 500 : 410}><BarChart data={rows} layout={horizontal ? "vertical" : "horizontal"}
        margin={{top: 10, right: 24, left: horizontal ? 0 : 8, bottom: 8}}>
        <CartesianGrid strokeDasharray="3 3"/>
        <XAxis type={horizontal ? "number" : "category"} dataKey={horizontal ? undefined : "productName"}
            interval={horizontal ? "preserveEnd" : 0} angle={horizontal ? 0 : -45} textAnchor={horizontal ? "middle" : "end"}
            height={horizontal ? 30 : scrollColumns ? 150 : 112} tick={{fontSize: 11}} tickFormatter={horizontal ? amount : shortName}/>
        <YAxis type={horizontal ? "category" : "number"} dataKey={horizontal ? "productName" : undefined}
            width={horizontal ? 125 : 60} tick={{fontSize: 11}} tickFormatter={horizontal ? shortName : amount}/>
        {/* Moving HTML tooltips must not animate over the SVG, which can leave white paint trails. */}
        <Tooltip formatter={value => [amount(value), metric.label]} isAnimationActive={false}
            wrapperStyle={{transition: "none"}} cursor={{fill: "rgba(0, 136, 254, 0.06)"}}/>
        <Legend/>
        <Bar dataKey={metric.key} name={metric.label} maxBarSize={horizontal ? 32 : 140} fill={COLORS[0]}
            shape={<SolidBar/>} activeBar={<SolidBar/>} isAnimationActive={false}>
            {cells(rows)}
        </Bar>
    </BarChart></ResponsiveContainer></Box></Box>;
}

export function ProductChart({data, type, language, t, horizontal = false, scrollColumns = false}) {
    if (type === "pie") return <ProductPieChart data={data} colors={COLORS} language={language} t={t}/>;
    const metric = {key: type === "revenue" ? "totalRevenue" : "totalCount",
        label: t(type === "revenue" ? "DistributorSalesAmount" : "DistributorUnitsSold")};
    const rows = data.filter(row => finiteNumber(row[metric.key]));
    if (!rows.length) return <Alert severity="info">{t("DistributorMetricUnavailable")}</Alert>;
    const number = value => new Intl.NumberFormat(language || "en").format(value);
    if (rows.every(row => row[metric.key] === 0)) return <Typography>{metric.label}: {number(0)}</Typography>;
    const amount = value => (type === "revenue" && formatMoney(value, rows[0].currency, language)) || number(value);
    return <ProductBars rows={rows} metric={metric} amount={amount} horizontal={horizontal} scrollColumns={scrollColumns}/>;
}

export function SalesCharts({data, language, t, horizontal = false, all = false, scrollColumns = false}) {
    const groups = new Map();
    data.filter(row => finiteNumber(row.totalRevenue)).forEach(row => {
        const currency = formatMoney(0, row.currency, language) === null ? "" : row.currency;
        groups.set(currency, [...(groups.get(currency) || []), row]);
    });
    if (!groups.size) return <ProductChart data={[]} type="revenue" language={language} t={t}/>;
    // Rank only comparable amounts; unspecified currency never blocks plotting a valid amount.
    return [...groups].map(([currency, rows]) => <Box key={currency || "unspecified"} sx={{mb: 2}}>
        {groups.size > 1 && <Typography variant="subtitle2">{t("DistributorSalesAmount")} ({currency || t("DistributorCurrencyUnavailable")})</Typography>}
        <ProductChart data={horizontal ? [...rows].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, all ? rows.length : 5) : rows}
            type="revenue" language={language} t={t} horizontal={horizontal} scrollColumns={scrollColumns}/>
    </Box>);
}

export function CategoryChart({data, language, t}) {
    // A partial quantity total would give misleading percentages, so require every shown quantity.
    if (data.some(row => !finiteNumber(row.totalCount) || row.totalCount < 0)) {
        return <Alert severity="info">{t("DistributorMetricUnavailable")}</Alert>;
    }
    const categories = new Map();
    data.forEach(row => {
        const name = row.category ? t(row.category) : t("DistributorCategoryUnknown");
        categories.set(name, (categories.get(name) || 0) + row.totalCount);
    });
    const rows = [...categories].map(([name, value]) => ({name, value}));
    if (rows.every(row => row.value === 0)) return <Typography>{t("DistributorUnitsSold")}: 0</Typography>;
    const number = value => new Intl.NumberFormat(language || "en").format(value);
    const percent = value => new Intl.NumberFormat(language || "en", {style: "percent", maximumFractionDigits: 0}).format(value);
    return <ResponsiveContainer width="100%" height={320}>
        <PieChart><Pie data={rows} dataKey="value" nameKey="name" innerRadius="45%" outerRadius="75%"
            label={({percent: share}) => percent(share)} labelLine={false}>
            {cells(rows)}
        </Pie><Tooltip formatter={value => [number(value), t("DistributorUnitsSold")]} isAnimationActive={false}
            wrapperStyle={{transition: "none"}}/><Legend iconType="circle"/></PieChart>
    </ResponsiveContainer>;
}
